<template>
  <div>
    <div id="trend-plot-grid">
      <svg
        id="trend-title-text"
        viewBox="0 0 20.9 1"
        preserveAspectRatio="xMinYMin"
      >
        <text font-size="0.60pt" x="0.25" y="0.70" fill="#156560">
          Advanced Parameters
        </text>
      </svg>
      <TimeAxisSlim
        id="time-axis-slim"
        :startTime="startTime"
        :endTime="endTime">
      </TimeAxisSlim>
      <PercentChangePlot
        id="percent-change-plot"
        :markers="percentMarkers"
        :signals="percentSignal"
        :yMax="yMax"
        :yMin="yMin"
        :xMax="xMax"
        :xMin="xMin"
        :showDash="true"
      ></PercentChangePlot>
      <div id="y-scale-container">
        <svg id="y-scale-max-container" viewBox="0 0 10 10">
          <text x="2" y="3" font-size="2pt" text-align="right">
            {{ maxPercent }}
          </text>
        </svg>
        <svg id="y-scale-min-container" viewBox="0 0 10 10">
          <text x="2" y="8" font-size="2pt" text-align="right">
            {{ minPercent }}
          </text>
        </svg>
      </div>
      <div id="numeric-change">
        <div class="numeric-header">
          <div class="numeric-column">
            <span class="abbrev">CO</span>
            <span class="full-name">Cardiac Output</span>
          </div>
          <div class="numeric-column">
            <span class="abbrev">HR</span>
            <span class="full-name">Heart Rate</span>
          </div>
          <div class="numeric-column">
            <span class="abbrev">SVR</span>
            <span class="full-name">Systemic</span>
            <span class="full-name">Vascular</span>
            <span class="full-name">Resistance</span>
          </div>
        </div>
      </div>
      <div id="percent-numerics">
        <PercentNumeric
          id="co-numeric"
          :min="yMin"
          :max="yMax"
          :value="coNumeric"
          color="#1B77D2"
        ></PercentNumeric>
        <PercentNumeric
          id="hr-numeric"
          :min="yMin"
          :max="yMax"
          :value="hrNumeric"
          color="#fcbc36"
        ></PercentNumeric>
        <PercentNumeric
          :min="yMin"
          :max="yMax"
          :value="svrNumeric"
          color="#d345da"
        ></PercentNumeric>
      </div>
    </div>
  </div>
</template>

<script>
import PercentChangePlot from '../components/PercentChangePlot'
import PercentNumeric from '../components/PercentNumeric'
import TimeAxisSlim from '../components/TimeAxisSlim'

export default {
  data() {
    return {}
  },
  props: {
    xMin: {
      type: Number,
      required: true
    },
    xMax: {
      type: Number,
      required: true
    },
    startTime: {
      type: Date,
      required: true
    },
    endTime: {
      type: Date,
      required: true
    },
    coSignal: {
      type: Array,
      required: true
    },
    hrSignal: {
      type: Array,
      required: true
    },
    svrSignal: {
      type: Array,
      required: true
    },
    markers: {
      type: Array,
      required: false,
      default: null
    }
  },
  computed: {
    durationMins() {
      return 15
    },
    coNumeric() {
      if (!this.coSignal || this.coSignal.length === 0) return 0
      let baseline = this.signalBaseline(this.coSignal)
      if (isNaN(baseline)) return 0
      return Math.round(
        ((this.coSignal[this.coSignal.length - 1][1] - baseline) / baseline) *
          100
      )
    },
    hrNumeric() {
      if (!this.hrSignal || this.hrSignal.length === 0) return 0
      let baseline = this.signalBaseline(this.hrSignal)
      if (isNaN(baseline)) return 0
      return Math.round(
        ((this.hrSignal[this.hrSignal.length - 1][1] - baseline) / baseline) *
          100
      )
    },
    svrNumeric() {
      if (!this.svrSignal || this.svrSignal.length === 0) return 0
      let baseline = this.signalBaseline(this.svrSignal)
      if (isNaN(baseline)) return 0
      return Math.round(
        ((this.svrSignal[this.svrSignal.length - 1][1] - baseline) / baseline) *
          100
      )
    },
    percentSignal() {
      return this.percentCombo
    },
    percentCombo() {
      return [
        { points: this.convertToPercent(this.coSignal), color: '#1B77D2' },
        { points: this.convertToPercent(this.hrSignal), color: '#fcbc36' },
        { points: this.convertToPercent(this.svrSignal), color: '#d345da' }
      ]
    },
    percentCO() {
      return [
        { points: this.convertToPercent(this.coSignal), color: '#1B77D2' }
      ]
    },
    percentHR() {
      return [
        { points: this.convertToPercent(this.hrSignal), color: '#fcbc36' }
      ]
    },
    percentSVR() {
      return [
        { points: this.convertToPercent(this.svrSignal), color: '#d345da' }
      ]
    },
    yMagnitude() {
      //todo: check max over all displayed signals not just first in list, to handle comboSignal
      let maxAbs = 50
      this.percentSignal.forEach(signal => {
        let signalMin = Math.abs(this.signalMin(signal.points))
        let signalMax = Math.abs(this.signalMax(signal.points))
        if (signalMin > maxAbs) maxAbs = signalMin
        if (signalMax > maxAbs) maxAbs = signalMax
      })
      return maxAbs
    },
    yMax() {
      return this.yMagnitude
    },
    yMin() {
      return -this.yMagnitude
    },
    percentMarkers() {
      // Checking within range of percentSignal as we can only plot
      // markers if they fall on the signal
      if (!this.markers || !this.percentSignal || this.percentSignal.length === 0 ||
          !this.percentSignal[0].points || this.percentSignal[0].points.length === 0) {
        return []
      }
      let markersInRange = this.markers.filter(
        marker =>
          marker.point[0] >= this.xMin &&
          marker.point[0] <= this.xMax &&
          marker.point[0] >= this.percentSignal[0].points[0][0] &&
          marker.point[0] <=
            this.percentSignal[0].points[
              this.percentSignal[0].points.length - 1
            ][0]
      )
      if (markersInRange.length === 0) {
        return []
      }
      return markersInRange.map(marker => {
        let markerY = this.percentSignal[0].points.find(
          point => point[0] === marker.point[0]
        )[1]
        return {
          point: [marker.point[0], markerY],
          type: marker.type,
          shortName: marker.shortName,
          isFlipped: markerY > 0
        }
      })
    },
    maxPercent() {
      return '+' + Math.round(this.yMax) + '%'
    },
    minPercent() {
      return Math.round(this.yMin) + '%'
    },
    currentColor() {
      return '#1B77D2'
    }
  },
  components: {
    PercentChangePlot,
    PercentNumeric,
    TimeAxisSlim
  },
  methods: {
    convertToPercent(signal) {
      if (!signal || signal.length === 0) return []
      let baseline = this.signalBaseline(signal)
      if (isNaN(baseline)) return []
      return signal.map(point => {
        let percent = ((point[1] - baseline) / baseline) * 100
        return [point[0], percent]
      })
    },
    signalBaseline(signal) {
      for (let i = 0; i < signal.length; i++) {
        if (signal[i][0] >= this.xMin && !isNaN(signal[i][1])) {
          return signal[i][1]
        }
      }
      return NaN
    },
    signalMin(signal) {
      let min = 0
      signal.forEach(element => {
        if (isFinite(element[1]) && element[1] < min) {
          min = element[1]
        }
      })
      return min
    },
    signalMax(signal) {
      let max = 0
      signal.forEach(element => {
        if (isFinite(element[1]) && element[1] > max) {
          max = element[1]
        }
      })
      return max
    }
  }
}
</script>

<style scoped>
#trend-plot-grid {
  background-color: white;
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-rows: 17.6% 10% 72.4%;
  grid-template-columns: 6% 50.5% 25% 18.5%;
  border: 1px solid #d3d3d3;
  box-sizing: border-box;
}
#trend-title-text {
  grid-row: 1;
  grid-column-start: 1;
  grid-column-end: 4;
  align-self: start;
}
#time-axis-slim {
  grid-row: 2;
  grid-column-start: 2;
  grid-column-end: 4;
}
#percent-change-plot {
  padding-top: 1.5%;
  padding-bottom: 1.5%;
  grid-row: 3;
  background: #eeeeee;
  border: 1px solid #979797;
  grid-column-start: 2;
  grid-column-end: 4;
}
#y-scale-container {
  grid-row: 3;
  grid-column: 1;
  background: #eeeeee;
  border: 1px solid #979797;
  position: relative;
}
#y-scale-max-container {
  position: absolute;
  top: 0;
  left: 0;
}
#y-scale-min-container {
  position: absolute;
  bottom: 0;
  left: 0;
}
#numeric-change {
  grid-row-start: 1;
  grid-row-end: 3;
  grid-column: 4;
  background-color: #d3d3d3;
  display: flex;
  flex-direction: column;
}
.numeric-header {
  display: flex;
  justify-content: space-around;
  align-items: flex-start;
  height: 100%;
  padding: 4px 2px;
}
.numeric-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.abbrev {
  font-size: 1.1em;
  font-weight: bold;
}
.full-name {
  font-size: 0.6em;
  line-height: 1.1;
}
#percent-numerics {
  grid-row-start: 3;
  grid-row-end: 4;
  grid-column: 4;
  display: flex;
  justify-content: space-between;
  background-color: #d3d3d3;
}
#percent-numeric-single {
  grid-row: 2;
  grid-column: 4;
  display: grid;
  grid-template-columns: 25% 50% 25%;
  grid-template-rows: 100%;
  background-color: #d3d3d3;
}
#percent-numeric-single-item {
  grid-column: 2;
}
#co-numeric {
  border-right: 1px solid black;
}
#hr-numeric {
  border-right: 1px solid black;
}
</style>
